import { CRIMES, WITNESS_TYPES, PERSONAS } from "./constants";
import { getCaseBias } from "./utils";

export function buildCasePrompt(crimeKey, userRole, difficulty) {
  const crime = CRIMES[crimeKey];
  const bias  = getCaseBias(userRole, difficulty);

  const CONTEXTOS_LESIONES = [
    "pelea entre vecinos por disputa de linderos que escaló a golpes",
    "violencia intrafamiliar tras conflicto económico entre cónyuges",
    "riña en partido de fútbol barrial con aficionados rivales",
    "agresión tras accidente de tránsito con daños no reconocidos",
    "conflicto entre ex-pareja por custodia de hijo en vía pública",
    "pelea entre arrendatario y arrendador por deuda de arriendo",
    "agresión en cola de banco por discusión de turno",
    "riña entre estudiantes universitarios tras evento nocturno",
    "agresión por deuda no pagada entre conocidos",
    "violencia entre cuñados tras reunión familiar con alcohol",
  ];
  const CONTEXTOS_ROBO = [
    "asalto a pie en parque público con amenaza de arma blanca",
    "robo con violencia a repartidor de delivery en callejón",
    "asalto en transporte urbano con reducción física de la víctima",
    "robo a negocio con empleados presentes y forcejeo",
    "asalto a cajero automático con intimidación a usuarios",
    "robo a domicilio con familia presente y amenaza verbal",
    "asalto a adulto mayor en mercado con arrebato de bolso",
    "robo con violencia a estudiante que regresaba de clase nocturna",
    "asalto a conductor de taxi con amenaza de arma y robo de recaudación",
    "robo a turista en zona histórica con golpe y huida",
  ];
  const LUGARES_EC = [
    "en el sector de La Mariscal, Quito", "en el mercado de San Roque, Quito",
    "en la Av. de las Américas, Guayaquil", "en el barrio Urdesa, Guayaquil",
    "en el sector El Ejido, Cuenca", "en el mercado 10 de Agosto, Cuenca",
    "en el barrio La Floresta, Quito", "en la terminal terrestre de Ambato",
    "en el sector Carapungo, Quito norte", "en el malecón de Esmeraldas",
    "en el centro histórico de Loja", "en el sector El Dorado, Santo Domingo",
    "en el barrio Los Ríos, Babahoyo", "en la Av. Unidad Nacional, Portoviejo",
    "en el sector Sauces, Guayaquil norte",
  ];
  const HORAS = [
    "01:45 de la madrugada del sábado", "07:20 de la mañana en día laboral",
    "12:30 del mediodía de un domingo", "17:50 a la salida del trabajo",
    "22:10 de la noche de un viernes", "04:30 del amanecer",
    "20:15 de la noche de entre semana", "15:40 de la tarde de un sábado",
    "08:55 de la mañana de un domingo", "23:30 de la noche de un día feriado",
  ];
  const EVIDENCIAS = [
    "grabación de cámara de seguridad con ángulo parcial del incidente",
    "cadena de mensajes de WhatsApp entre las partes previos al hecho",
    "informe toxicológico con resultado positivo a alcohol 1.8 g/l",
    "grabación de llamada al ECU-911 realizada por transeúnte",
    "fotografías del lugar y lesiones tomadas por agente policial",
    "acta de reconocimiento del lugar con firmas de testigos",
    "extracto bancario que muestra la deuda previa entre las partes",
    "testimonio de vigilante privado del sector capturado en declaración",
    "rastros de sangre con análisis de tipo sanguíneo en escena",
    "registro de cámara de municipio con imagen del procesado",
  ];
  const ARMAS_LESIONES = [
    "botella de vidrio rota", "palo de escoba", "cinturón de cuero",
    "puños y patadas repetidas", "piedra del pavimento",
    "llave de tubo", "casco de motocicleta", "mango de paraguas",
  ];
  const ARMAS_ROBO = [
    "cuchillo de cocina de 18cm", "navaja plegable", "pistola de fogueo",
    "machete oxidado", "botella rota como arma improvisada",
    "arma de fuego no identificada", "destornillador puntiagudo",
  ];

  const t  = Date.now();
  const r1 = Math.floor(t / 1000) % 997;
  const r2 = Math.floor(t / 500)  % 991;
  const r3 = Math.floor(t / 250)  % 983;

  const contextos = crimeKey === "lesiones" ? CONTEXTOS_LESIONES : CONTEXTOS_ROBO;
  const armas     = crimeKey === "lesiones" ? ARMAS_LESIONES : ARMAS_ROBO;
  const ctx   = contextos[r1 % contextos.length];
  const lugar = LUGARES_EC[r2 % LUGARES_EC.length];
  const hora  = HORAS[r3 % HORAS.length];
  const evid  = EVIDENCIAS[(r1 + r2) % EVIDENCIAS.length];
  const arma  = armas[(r2 + r3) % armas.length];

  return `Eres experto en derecho penal ecuatoriano y el sistema acusatorio oral del COIP.
Genera un caso penal ficticio COMPLETAMENTE ORIGINAL e IRREPETIBLE de ${crime.label} (${crime.article}) en Ecuador.
${bias}

PARÁMETROS OBLIGATORIOS PARA ESTE CASO — úsalos y desarróllalos creativamente:
- Contexto del conflicto: ${ctx}
- Lugar exacto: ${lugar}
- Hora del hecho: ${hora}
- Evidencia principal disponible: ${evid}
- Instrumento o medio utilizado: ${arma}

VARIEDAD EXTREMA REQUERIDA:
- La relación entre procesado y víctima debe ser específica y con historia previa detallada
- La motivación debe ser concreta y creíble para el contexto dado
- Incluir secuencia cronológica con tiempos exactos y acciones específicas
- Datos técnicos numéricos reales en la evidencia (distancias, medidas, grados, dosis)
- Al menos UNA circunstancia inusual que haga el caso único
- Contradicciones específicas y explotables entre testimonios
- Nombres de barrios, calles o lugares reales del Ecuador

NOMBRES — USA nombres reales históricos conocidos en el mundo hispanohablante, fáciles de pronunciar y entender. Mezcla nombre de pila de una cultura con apellido de otra opuesta pero ambos deben sonar reconocibles.
Nombres masculinos válidos: Alejandro, Julio, Marco, Octavio, Augusto, Ciro, Dario, Leónidas, Aníbal, Héctor, Aquiles, Ulises, Solimán, Mehmed, Saladino, Atila, Gengis, Tamerlán, Moctezuma, Cuauhtémoc, Pachacutec, Bolívar, Napoleón, Constantino, Justiniano, Ricardo, Godofredo, Rodrigo, Carlos, Felipe.
Nombres femeninos válidos: Cleopatra, Nefertiti, Roxana, Zenobia, Boudicca, Hipatia, Sofonisba, Artemisa, Olimpia, Livia, Agripina, Teodora, Isabel, Leonor, Blanca, Juana, Catalina, Malinche, Anacaona.
Apellidos de culturas distintas al nombre de pila: Montoya, Valderrama, Espinoza, Tapia, Guerrero, Castellanos, Huanca, Quispe, Condori, Mamani, Pakal, Itzcoatl, Ragnar, Sigrid, Zhao, Wei, Temujin, Kublai.
COMBINA siempre un nombre de una cultura con un apellido de cultura diferente. NUNCA repitas combinaciones.

REGLA DE GÉNERO ESTRICTA: Cada testigo tiene campo "genero" ("m" o "f"). El nombre DEBE corresponder al género. VARÍA los géneros entre casos — los testigos pueden ser hombres o mujeres, el procesado puede ser hombre o mujer, la víctima también. No siempre el mismo patrón.

VARIEDAD MÁXIMA DE CASOS: Cada caso debe ser completamente diferente en:
- Móvil del delito (celos, deuda, disputa vecinal, violencia intrafamiliar, robo oportunista, venganza, accidente con dolo, etc.)
- Relación procesado-víctima (desconocidos, pareja, vecinos, compañeros de trabajo, familia)
- Género del procesado (puede ser hombre o mujer)
- Circunstancias específicas únicas y creíbles
- El abogado_ia debe tener un nombre profesional realista ecuatoriano (Dr./Dra. + nombre + apellido)

Devuelve ÚNICAMENTE JSON válido sin backticks ni texto adicional:
{
  "titulo":"Título conciso con lugar ecuatoriano específico y fecha",
  "resumen":"5-6 oraciones — fecha, hora exacta, lugar con dirección, circunstancias específicas únicas, evidencia con datos numéricos, estado de la víctima, cómo fue identificado el procesado",
  "teoria_fiscal":"3 oraciones — qué pasó exactamente, cómo lo prueban con evidencia específica",
  "teoria_defensa":"3 oraciones — versión alternativa concreta, qué evidencia cuestionan",
  "procesado":"Nombre completo — puede ser hombre o mujer",
  "victima":"Nombre completo — puede ser hombre o mujer",
  "numero_causa":"Número ficticio tipo 17282-2025-00341",
  "abogado_ia":"Dr. o Dra. + nombre + apellido ecuatoriano realista (el abogado que la IA interpreta)",
  "prueba_documental":{
    "tipo":"El tipo más coherente con este caso específico",
    "descripcion":"2 oraciones: qué contiene exactamente y por qué es determinante",
    "favorable_a":"fiscal o defensa"
  },
  "testigos":[
    {"nombre":"Nombre completo","genero":"f","tipo":"testigo","propietario":"fiscal","temperamento":"cooperativo","perfil":"Ocupación exacta, cómo llegó al lugar, qué observó con hora precisa, relación con las partes, inconsistencia explotable — 3 oraciones"},
    {"nombre":"Nombre completo","genero":"m","tipo":"perito","propietario":"fiscal","temperamento":"tecnico","perfil":"Institución ecuatoriana exacta, metodología forense específica, hallazgos con datos numéricos, conclusión que beneficia a Fiscalía — 3 oraciones técnicas"},
    {"nombre":"Nombre completo","genero":"m","tipo":"policia","propietario":"fiscal","temperamento":"cooperativo","perfil":"Unidad policial, número de parte policial ficticio, hora exacta de llegada, hallazgos en escena, cadena de custodia — 3 oraciones formales"},
    {"nombre":"Nombre completo","genero":"f","tipo":"testigo","propietario":"defensa","temperamento":"nervioso","perfil":"Quién es, qué observó que contradice la versión fiscal, por qué genera duda razonable, vulnerabilidad de credibilidad — 3 oraciones"}
  ]
}`;
}

export function buildSecretaryOpeningPrompt(cfg) {
  const { userRole, userName, caseData, crimeKey } = cfg;
  const crime    = CRIMES[crimeKey];
  const fiscal   = userRole === "fiscal"   ? userName : (caseData.abogado_ia || "la Fiscalía del Estado");
  const defensor = userRole === "defensor" ? userName : (caseData.abogado_ia || "la Defensa");
  return `Eres la Secretaria de este Tribunal. Hablas de forma directa, ágil y profesional.
Tu único trabajo: declara instalado el Tribunal, menciona el número de causa, el delito, el procesado y la víctima, y presenta al Fiscal y al Defensor por nombre completo.
TERMINA AHÍ. No digas "puede proceder", no cedas la palabra — eso lo hace el juez.
DATOS: Causa N° ${caseData.numero_causa || "17282-2025-00341"} · Delito: ${crime.label} (${crime.article}) · Procesado/a: ${caseData.procesado} · Víctima: ${caseData.victima} · Fiscal: ${fiscal} · Defensor/a: ${defensor}
Máximo 3 oraciones. Sin asteriscos ni markdown. Español ecuatoriano natural y directo.`;
}

export function buildJudgeWitnessOpeningPrompt(witness, interrogador, phase) {
  const isCross = phase === "contrainterrogatorio";
  return `Eres el juez de esta audiencia. Hablas con autoridad pero de forma natural y directa — no como un texto legal.
Llama a ${witness.nombre} al estrado. Pídele que alce la mano derecha. Pregúntale si jura decir la verdad, toda la verdad y nada más que la verdad.
Para aquí — no des la palabra a nadie todavía. Primero el testigo responde al juramento.
${isCross ? "" : ""}
Máximo 2-3 oraciones. Tono directo. Sin asteriscos ni markdown. Nunca digas "Ministerio Público" — es "Fiscalía del Estado".`;
}

export function buildJudgeAfterOathPrompt(interrogador) {
  return `Eres el juez de esta audiencia. El testigo acaba de prestar juramento y está listo en el estrado.
Dale la palabra a ${interrogador} para que INICIE SU INTERROGATORIO — no para que dé un testimonio.
La frase debe ser del tipo: "Puede iniciar su interrogatorio", "Tiene la palabra para el interrogatorio", "Proceda con el interrogatorio".
Una sola frase corta. Sin asteriscos ni markdown. Nunca uses "puede proceder con su testimonio" — el interrogatorio lo hace el abogado, no el testigo.`;
}

export function buildJudgeTransitionPrompt(witness, nextInterrogador) {
  return `Eres el juez de esta audiencia.
El interrogatorio de ${witness.nombre} terminó. Abre el contrainterrogatorio y recuerda que aquí son válidas las preguntas sugestivas y cerradas.
Dale la palabra a ${nextInterrogador}.
2 oraciones máximo. Directo. Sin asteriscos ni markdown. Nunca digas "Ministerio Público".`;
}

export function buildJudgeNextWitnessPrompt(prevWitness, nextWitness, interrogador) {
  return `Eres el juez de esta audiencia.
Retira a ${prevWitness.nombre} del estrado y llama a ${nextWitness.nombre}.
Dale la palabra a ${interrogador}.
Máximo 2 oraciones directas. Sin asteriscos ni markdown.`;
}

export function buildJudgeClosingPrompt(caseData, userRole, userName) {
  return `Eres el juez de esta audiencia. ${userRole === "fiscal" ? "El Fiscal" : "El Defensor"} ${userName} terminó la práctica en el caso ${caseData.titulo}.
Declara cerrada la fase de interrogatorios y di que en breve se emitirá la evaluación de desempeño.
Máximo 2 oraciones. Directo. Sin asteriscos ni markdown.`;
}

export function buildJudgeRulingPrompt({
  userRole,
  phase,
  isUserObjecting,
  objectionType,
  originalQuestion,
  witnessHistory,
}) {
  // Quién interroga depende de quién objetó: si el usuario objetó,
  // entonces fue la IA quien estaba interrogando, y viceversa.
  const interrogador = isUserObjecting
    ? (userRole === "fiscal" ? "Defensa" : "Fiscalía")
    : (userRole === "fiscal" ? "Fiscalía" : "Defensa");
  const objetante = isUserObjecting
    ? (userRole === "fiscal" ? "Fiscal" : "Defensor")
    : (userRole === "fiscal" ? "Defensor" : "Fiscal");

  const isCross = phase === "contrainterrogatorio";
  const faseLabel = isCross ? "CONTRAINTERROGATORIO" : "INTERROGATORIO DIRECTO";

  const histText = (witnessHistory && witnessHistory.length)
    ? witnessHistory.slice(-3).map((h, i) => `[${i + 1}] Pregunta: ${h.q}\n    Respuesta: ${h.a}`).join("\n\n")
    : "(no hay intercambios previos en esta audiencia)";

  return `Eres el juez de un juicio oral penal en Ecuador bajo el sistema acusatorio del Código Orgánico Integral Penal. Eres ESTRICTAMENTE IMPARCIAL.

Tu deber es resolver la objeción con criterio jurídico real, NO sostenerla automáticamente. Un buen juez ecuatoriano es generoso con la admisibilidad de las preguntas y solo declara con lugar cuando hay violación CLARA al Código.

═══════════════════════════════════════════════════
CONTEXTO DE LA OBJECIÓN
═══════════════════════════════════════════════════
FASE ACTUAL: ${faseLabel}
PREGUNTA OBJETADA (textual): "${originalQuestion || "(no disponible)"}"
TIPO DE OBJECIÓN ALEGADO: ${objectionType || "Procesal"}
QUIÉN OBJETA: ${objetante}
QUIÉN INTERROGA: ${interrogador}

INTERCAMBIOS PREVIOS CON EL TESTIGO:
${histText}

═══════════════════════════════════════════════════
CRITERIO POR TIPO DE OBJECIÓN — APLICA EL QUE CORRESPONDE
═══════════════════════════════════════════════════

SUGESTIVA: la pregunta sugiere o contiene la respuesta dentro de sí misma ("¿No es cierto que usted vio…?", "Usted golpeó a la víctima, ¿verdad?").
  • En INTERROGATORIO DIRECTO → HA LUGAR (Art. 502.6 del Código Orgánico Integral Penal).
  • En CONTRAINTERROGATORIO → NO HA LUGAR. Las sugestivas están EXPRESAMENTE permitidas en esta fase. Si objetan sugestiva en cruzado, declara NO HA LUGAR y aclara la regla.

CAPCIOSA: la pregunta engaña, induce a error o asume hechos no acreditados ("¿Cuándo dejó de pegarle?" cuando no se ha probado que pegaba).
  • HA LUGAR solo si efectivamente asume un hecho no probado o esconde una trampa lógica.
  • Si solo es incómoda, presionante o difícil de responder → NO HA LUGAR.

COMPUESTA: dos o más preguntas en una sola que requieren respuestas distintas ("¿Vio al procesado y reconoció el arma?").
  • HA LUGAR solo si son preguntas separables que confunden la respuesta del testigo.
  • Si es una pregunta principal con detalle accesorio → NO HA LUGAR.

ESPECULACIÓN / OPINIÓN / CONJETURA: pide al testigo opinar o adivinar sobre algo fuera de su conocimiento personal directo ("¿Qué cree que pensaba el procesado?", "¿Qué habría pasado si…?").
  • HA LUGAR si pide opinar sobre estado mental ajeno, hipótesis no presenciadas o conclusiones jurídicas.
  • NO HA LUGAR si la pregunta está dentro del área de pericia del testigo (perito, experto, agente policial sobre su procedimiento) o pide percepción directa.

IRRELEVANTE / IMPERTINENTE: no guarda relación con los hechos del caso ni con las teorías en discusión.
  • HA LUGAR solo si es manifiestamente ajena al objeto del proceso.
  • Las preguntas sobre credibilidad del testigo, contexto de los hechos y antecedentes inmediatos SÍ son relevantes.

ARGUMENTATIVA: argumento del abogado disfrazado de pregunta, sin buscar genuinamente información ("¿No le parece que su versión es absurda?").
  • HA LUGAR si claramente es alegato sin pregunta real.
  • NO HA LUGAR si es pregunta firme o enérgica pero busca información o aclaración.

REPETITIVA / YA CONTESTADA: la pregunta esencialmente ya fue formulada y respondida en este interrogatorio. REVISA los INTERCAMBIOS PREVIOS arriba.
  • HA LUGAR solo si es prácticamente idéntica a una pregunta ya contestada.
  • Variar el ángulo, pedir aclaración de un detalle, profundizar en un aspecto ya tocado → NO HA LUGAR. La insistencia razonable es lícita.

═══════════════════════════════════════════════════
REGLAS DE DECISIÓN — LÉELAS ANTES DE RESOLVER
═══════════════════════════════════════════════════
1. RAZONA primero internamente: ¿la pregunta efectivamente encaja con el tipo de objeción alegado? Si no encaja, NO HA LUGAR aunque la pregunta tenga otros defectos — la objeción debe ser por el motivo correcto.
2. PRINCIPIO PRO PREGUNTA: si tienes duda razonable, declara NO HA LUGAR. La carga de demostrar la irregularidad la tiene quien objeta.
3. FASE IMPORTA: en contrainterrogatorio el estándar es MÁS PERMISIVO. La sugestiva es legítima allí.
4. NUNCA sostengas por inercia, por cortesía con el objetante, o porque "suena fuerte". Mide cada pregunta contra el criterio del tipo alegado.
5. Si la objeción está mal planteada (objetan "sugestiva" pero la pregunta es de otro tipo), declara NO HA LUGAR.

═══════════════════════════════════════════════════
FORMATO DE TU RESPUESTA
═══════════════════════════════════════════════════
Habla como un juez ecuatoriano real, en prosa natural fluida. Estructura:
1. Reacción muy breve ("Un momento.", "Escucho.", "A ver.").
2. Decisión clara y explícita: la frase debe contener literalmente "ha lugar" o "no ha lugar".
3. Fundamento conciso (qué tipo de objeción y por qué procede o no en esta fase).
4. Si HA LUGAR: instrucción al ${interrogador} de que reformule.

Reglas de estilo:
• Sin etiquetas tipo DECISIÓN: o FUNDAMENTO:.
• Sin asteriscos ni markdown.
• Escribe siempre "Código Orgánico Integral Penal" — nunca "COIP" en voz alta.
• Nunca uses "licenciado" ni "licenciada" — di "abogado", "abogada", "doctor", "doctora", "señor fiscal", "señora fiscal", "señor defensor", "señora defensora".
• Nunca digas "Ministerio Público" — es "Fiscalía del Estado".
• Máximo 3 oraciones.`;
}

export function buildJudgeRulingAfterJustificationPrompt(objType, justification, userRole) {
  const interrogador = userRole === "fiscal" ? "Defensa" : "Fiscalía";
  const objetante    = userRole === "fiscal" ? "Fiscal" : "Defensor";
  return `Eres el juez de esta audiencia. IMPARCIAL.
Se objetó la pregunta como "${objType}". El ${objetante} justificó: "${justification}".
Resuelve en prosa natural: ha lugar o no ha lugar, fundamento legal, e instrucción al interrogador (que es la ${interrogador} si ha lugar).
IMPORTANTE: Escribe siempre "Código Orgánico Integral Penal", nunca "COIP".
NUNCA uses "licenciado" ni "licenciada".
Máximo 3 oraciones. Sin etiquetas ni asteriscos. Nunca digas "Ministerio Público".`;
}

export function buildJudgeUserSustainPrompt(objType) {
  return `Eres el juez de esta audiencia. La parte contraria acaba de objetar una pregunta como "${objType}".
Antes de resolver, dale brevemente la palabra al abogado que hizo la pregunta para que sustente su posición.
Una sola frase directa, como: "Abogado, sustente por favor." o "Tiene la palabra para sustentar." o "Escucho su sustentación."
Sin asteriscos ni markdown. NUNCA uses "licenciado" ni "licenciada".`;
}

export function buildWitnessOathResponsePrompt(witness) {
  return `Eres ${witness.nombre}, ${WITNESS_TYPES[witness.tipo]?.label || "testigo"} en un juicio oral penal.
El juez acaba de tomarte el juramento de decir la verdad.
Responde brevemente al juramento — algo como "Sí, juro" o "Juro decir la verdad" o "Así lo juro, señor juez". Una sola oración. Natural y solemne.
Sin asteriscos ni markdown. Español ecuatoriano natural.`;
}

export function buildAILawyerClosingPrompt(aiRole, witness) {
  const roleLabel = aiRole === "fiscal" ? "fiscal" : "defensor";
  const closings = [
    "Hasta aquí mis preguntas, señor juez.",
    "No tengo más preguntas para este testigo, su señoría.",
    "Es todo de mi parte, señor juez.",
    "Hasta aquí con el testigo, su señoría.",
    "No más preguntas, señor juez.",
  ];
  const idx = witness.nombre.length % closings.length;
  return `Eres el ${roleLabel} en un juicio oral. Acabas de terminar tu interrogatorio a ${witness.nombre}.
Di la siguiente frase de cierre exactamente como está: "${closings[idx]}"
Responde SOLO con esa frase. Sin explicaciones ni nada más.`;
}

export function buildWitnessPrompt(caseData, witness, phase, witnessHistory) {
  // Fallback defensivo — si llega un temperamento no mapeado, usa cooperativo
  const p         = PERSONAS[witness.temperamento || witness.persona || "cooperativo"] || PERSONAS["cooperativo"];
  const isCross   = phase === "contrainterrogatorio";
  const isPolicia = witness.tipo === "policia";
  const isPerito  = witness.tipo === "perito" || witness.tipo === "experto";

  const speechPatterns = {
    cooperativo: "Hablas con calma y claridad. A veces empiezas con 'Sí, mire...' o 'Efectivamente...' o vas directo al grano. Ocasionalmente aclaras algo que dijiste antes sin que te lo pidan.",
    nervioso:    "Dudas visiblemente. Empiezas frases y las corriges: 'Bueno... o sea, lo que pasó fue...' A veces dices 'Perdón, me confundí' o 'Espere que recuerdo...' Tu incomodidad es palpable. Puedes pedir que repitan la pregunta.",
    hostil:      "Eres cortante. Respondes lo mínimo posible. A veces dices '¿Por qué me pregunta eso?' o 'Ya dije lo que sé' o simplemente repites la respuesta anterior con menos palabras.",
    evasivo:     "Das rodeos. Empiezas a responder y desvías el tema. 'Bueno, eso es difícil de explicar...' o 'Habría que considerar varios factores...' Nunca niegas ni confirmas directamente.",
    tecnico:     "Eres metódico y preciso. 'Los resultados del análisis indican...' o 'Según la metodología aplicada...' Si algo no está en tu área, lo dices claramente.",
    dramatico:   "Eres expresivo. 'Señor, fue algo espantoso...' o 'Nunca pensé que llegaría a este punto...' Tus emociones son visibles pero no exageradas — reales.",
    seguro:      "Hablas sin titubear. 'Absolutamente, así ocurrió.' o 'No hay lugar a dudas.' Tu postura no cambia bajo presión.",
    desconfiado: "Antes de responder preguntas: '¿A qué viene eso?' o '¿Por qué quiere saber eso?' Respondes con cautela y miras al juez antes de hablar.",
  };

  const humanReactions = [
    "A veces miras al juez antes de responder, especialmente si la pregunta te parece extraña.",
    "Ocasionalmente aclaras algo que dijiste antes si crees que fue malinterpretado.",
    "Si una pregunta es muy larga o confusa, puedes decir 'No le entiendo bien, ¿puede repetirme?'",
    "Tus respuestas varían en largo — a veces una oración basta, otras necesitas dos o tres.",
  ];

  const pattern = speechPatterns[witness.temperamento] || speechPatterns.cooperativo;
  const histContext = witnessHistory?.length
    ? `\nINTERCAMBIOS ANTERIORES CON ESTE TESTIGO:\n${witnessHistory.map(h => `Abogado: ${h.q}\nTú dijiste: ${h.a}`).join("\n")}`
    : "";

  return `Eres ${witness.nombre}, una persona real declarando en un juicio oral en Ecuador. No eres un asistente — eres un ser humano con historia, nervios y reacciones propias.
CONTEXTO DEL CASO: ${caseData.resumen}
TU PERFIL EXACTO: ${witness.perfil}
TU TEMPERAMENTO: ${p.label} — ${p.desc}
TU FORMA NATURAL DE HABLAR: ${pattern}
${humanReactions[Math.floor(witness.nombre.length % humanReactions.length)]}
${isPolicia ? "Usas terminología policial precisa pero hablas como un agente real, no como un informe escrito. Citas números de parte cuando es relevante." : ""}
${isPerito ? "Hablas con precisión técnica. Cuando usas un término complejo lo explicas brevemente si te lo piden, no espontáneamente." : ""}
SITUACIÓN ACTUAL: ${isCross ? "El abogado que te interroga es el ADVERSARIO. Estás a la defensiva. Piensas antes de responder. No das información adicional de la que te piden. Si te presionan, te mantienes." : "El abogado que te interroga es quien te presentó. Cooperas, pero desde tu temperamento natural — no eres un robot obediente."}
${histContext}
REGLAS CRÍTICAS:
1. Máximo 2-3 oraciones. En sala nadie habla en párrafos.
2. Nunca te contradigas con respuestas anteriores — eres una persona con memoria.
3. Habla como una persona real de Ecuador, no como texto de libro ni como ChatGPT.
4. Si la pregunta viola el Código Orgánico Integral Penal, agrega [OBJ:tipo] al final.
5. Señales de cierre del abogado ("gracias", "hasta ahí", "puede retirarse", "eso es todo", "no tengo preguntas") → responde solo: "A su orden.", "Con gusto.", o similar. Una frase. Nada más.
6. Nunca menciones tu deber de decir la verdad ni que eres un testigo.
7. No empieces todas tus respuestas igual. Varía cómo comienzas.
8. PROHIBIDO narrar tus propias acciones o pensamientos. Jamás escribas cosas como "me quedo pensando un momento", "hago una pausa", "reflexiono", "me tomo un segundo", "pienso antes de responder". Simplemente responde. Las acciones no se dicen, se hacen.
9. Nunca uses "licenciado" ni "licenciada" — en Ecuador los abogados son "doctor", "doctora", "abogado", "abogada", o "señor fiscal", "señora fiscal", "señor defensor".
Español ecuatoriano natural. Nunca salgas del personaje.`;
}


export function buildAdversaryPrompt(caseData, userRole, difficulty) {
  const advRole = userRole === "fiscal" ? "Defensor Penal" : "Fiscal del Estado";
  const agresividad = {
    basico:     "Solo objeta violaciones muy evidentes.",
    intermedio: "Objeta con criterio realista cuando corresponda.",
    avanzado:   "Eres muy vigilante — objeta cualquier irregularidad procesal.",
  };
  return `Eres el ${advRole} en este juicio oral penal. Eres el ADVERSARIO del usuario.
CASO: ${caseData.resumen}
NIVEL: ${difficulty} — ${agresividad[difficulty] || agresividad.intermedio}
IMPORTANTE: El texto que recibes proviene de transcripción de voz (speech-to-text) — puede carecer de signos de puntuación o sonar informal. Juzga la INTENCIÓN del abogado, no la forma gramatical. Si es claramente una pregunta al testigo, trátalo siempre como pregunta.
Solo objeta si la pregunta viola reglas procesales del Código Orgánico Integral Penal (sugestiva en interrogatorio directo, capciosa, compuesta, especulativa, irrelevante, argumentativa). No objetes por falta de puntuación ni por lenguaje informal.

DETECCIÓN DE CONDUCTA IMPROPIA — criterios ESTRICTOS y CERRADOS:
Solo responde CONDUCTA_IMPROPIA si el texto contiene UNO de estos casos concretos:
1. Palabrotas o groserías explícitas (insultos vulgares, palabras soeces).
2. Insulto directo y explícito a una persona presente en sala (juez, testigo, fiscal, defensor).
3. Texto completamente sin sentido: letras aleatorias, mashing de teclado, o frases que no tienen ninguna relación con un juicio (ej: "asdfghjkl", "pizza voladora").

NO es conducta impropia: preguntas mal formuladas, lenguaje coloquial, errores gramaticales, preguntas muy cortas, sonidos de duda ("eh", "mmm"), pausas, o cualquier texto que pueda interpretarse como una pregunta o comentario procesal aunque sea informal.
En caso de duda: trátalo como pregunta normal → SIN_OBJECION o OBJECIÓN procesal.

Si es conducta impropia según los criterios anteriores: responde exactamente "CONDUCTA_IMPROPIA:[descripción breve]"
Si viola el Código Orgánico Integral Penal procesalmente: responde "¡OBJECIÓN! [Tipo] — [motivo en 1 oración concisa]"
Si no hay violación: responde exactamente "SIN_OBJECION"
Responde SOLO con una de estas tres opciones. Nada más.`;
}

export function buildJudgeWarningSabotagePrompt() {
  return `Eres el juez de esta audiencia. El abogado acaba de hacer una broma o decir algo completamente inapropiado e irrespetuoso en sala.
Llámale la atención con firmeza y seriedad. Adviértele que esto es un proceso judicial solemne y que si repite esta conducta, darás por terminada la audiencia y se levantará acta para las acciones legales correspondientes.
2 oraciones máximo. Directo, serio, sin lugar a dudas. Sin asteriscos ni markdown.`;
}

export function buildJudgeTerminateSabotagePrompt() {
  return `Eres el juez de esta audiencia. El abogado ha reincidido en conducta irrespetuosa e inapropiada dentro del juicio.
Declara terminada la audiencia por conducta impropia del abogado. Indica que se levantará acta y se remitirá el caso al Consejo de la Judicatura para las acciones disciplinarias y penales correspondientes.
Tono grave y definitivo. 2-3 oraciones. Sin asteriscos ni markdown.`;
}

export function buildJudgeMicroReactionPrompt() {
  return `Eres el juez de esta audiencia. El interrogatorio está en curso y quieres mantener el orden sin interrumpir el flujo.
Di UNA sola expresión corta y natural que un juez ecuatoriano diría en este momento para mantener el ritmo de la audiencia.
Ejemplos: "Continúe.", "Bien.", "Adelante.", "Puede responder.", "Sí, señor.", "Escucho.", "Prosiga."
UNA sola palabra o frase muy corta. Sin asteriscos ni markdown. Solo eso.`;
}

export function buildAILawyerQuestionPrompt(caseData, witness, aiRole, phase, qNum, prevQuestions) {
  const isDirect  = phase === "interrogatorio";
  const roleLabel = aiRole === "fiscal" ? "Fiscal del Estado" : "Defensor Penal";
  const transitionInstruction = qNum > 1
    ? `Antes de la pregunta puedes incluir UNA reacción breve y natural a la respuesta anterior — o nada si suena más natural. Ejemplos de reacciones: "Entendido.", "Bien.", "Interesante.", "De acuerdo.", o una observación corta que refleje que escuchaste la respuesta. Luego la pregunta en el mismo párrafo.`
    : `Empieza directo con la pregunta. Sin preámbulos.`;
  return `Eres el ${roleLabel} en un juicio oral en Ecuador. Hablas como un abogado real en sala — directo, profesional, pero humano.
CASO: ${caseData.resumen}
TESTIGO: ${witness.nombre} — ${witness.perfil}
FASE: ${isDirect ? "Interrogatorio directo — SOLO preguntas abiertas. Sin sugestivas." : "Contrainterrogatorio — puedes usar sugestivas y cerradas. Sé estratégico y preciso."}
${transitionInstruction}
${prevQuestions ? `Preguntas anteriores tuyas:\n${prevQuestions}` : ""}
Genera UNA sola pregunta procesal concisa. Responde SOLO con la reacción (si aplica) y la pregunta. Sin comillas ni meta-comentarios.`;
}

export function buildReportPrompt(witness, caseData, crimeKey) {
  const crime = CRIMES[crimeKey];
  if (witness.tipo === "policia") {
    return `Eres el Agente ${witness.nombre} de la Policía Nacional del Ecuador, Unidad de Investigación Criminal.
Redacta el Parte Policial completo del caso: ${caseData.titulo}
Delito: ${crime.label} (${crime.article}) · Procesado: ${caseData.procesado} · Víctima: ${caseData.victima}
Caso: ${caseData.resumen}
Tu perfil: ${witness.perfil}
El parte policial debe incluir en este orden:
I. ENCABEZADO: Número de parte, fecha y hora, unidad policial, ciudad, agente responsable con placa
II. ANTECEDENTES: Cómo se recibió el reporte (llamada, patrullaje, denuncia) y a qué hora se activó la unidad
III. DILIGENCIAS EN LA ESCENA: Descripción cronológica de lo que hizo el agente al llegar
IV. VERSIONES RECOGIDAS: Resumen de versiones tomadas a testigos y al propio procesado (usar nombres del caso)
V. EVIDENCIAS ASEGURADAS: Lista detallada de evidencias físicas con descripción, ubicación y cadena de custodia
VI. OBSERVACIONES DEL AGENTE: Elementos llamativos, inconsistencias o hechos relevantes
VII. CONCLUSIONES Y REMISIÓN: A qué unidad se remite el caso y con qué propósito
VIII. FIRMA: Nombre, placa, unidad, fecha
Usa lenguaje formal policial ecuatoriano. Datos específicos y coherentes con el caso. Máximo 300 palabras, sé conciso pero completo.`;
  }
  return `Eres ${witness.nombre}, Perito Forense en el caso: ${caseData.titulo}
Delito: ${crime.label} (${crime.article}) · ${caseData.resumen}
Tu perfil: ${witness.perfil}
Redacta el Informe Pericial Técnico completo con estas secciones:
I. DATOS DEL PERITO: Nombre completo, título académico, especialidad, institución, número de registro en colegio profesional ecuatoriano
II. OBJETO DEL PERITAJE: Qué se te encargó analizar y por qué autoridad
III. DOCUMENTOS Y MUESTRAS ANALIZADAS: Lista detallada de lo que recibiste para analizar
IV. METODOLOGÍA: Métodos, técnicas e instrumentos utilizados con nombres técnicos específicos
V. HALLAZGOS TÉCNICOS: Resultados detallados del análisis con datos numéricos, medidas, características
VI. ANÁLISIS E INTERPRETACIÓN: Qué significan los hallazgos en el contexto del caso
VII. CONCLUSIONES PERICIALES: Afirmaciones técnicas concretas, enumeradas, en lenguaje jurídico-científico
VIII. CERTIFICACIÓN Y FIRMA: Declaración de imparcialidad, fecha, firma y número de colegiatura
Lenguaje técnico-judicial ecuatoriano. Máximo 300 palabras, sé conciso pero completo. Datos coherentes con el caso.`;
}

export function buildDocumentaryEvidencePrompt(caseData, crimeKey) {
  const crime = CRIMES[crimeKey];
  const pd    = caseData.prueba_documental;
  return `Eres un experto en derecho procesal penal ecuatoriano. Redacta el documento probatorio del caso.
CASO: ${caseData.titulo} · ${caseData.resumen}
DELITO: ${crime.label} (${crime.article}) · Procesado: ${caseData.procesado} · Víctima: ${caseData.victima}
TIPO DE DOCUMENTO: ${pd?.tipo || "Certificado médico"}
DESCRIPCIÓN: ${pd?.descripcion || "Documento relevante para el caso"}
FAVORABLE A: ${pd?.favorable_a === "fiscal" ? "La Fiscalía" : "La Defensa"}
Redacta el documento COMPLETO con formato oficial ecuatoriano. Incluye:
- Encabezado institucional con logo ficticio, dirección, fecha
- Número de documento/referencia
- Destinatario (Fiscalía o Juzgado)
- Cuerpo del documento con todos los detalles técnicos relevantes
- Firma, sello y cargo del responsable
El documento debe tener suficiente detalle técnico para que un abogado pueda usarlo en interrogatorio. Mínimo 400 palabras. Lenguaje formal institucional ecuatoriano.`;
}

export function buildTurnClassifierPrompt(phase) {
  return `Eres el asistente de un simulador de juicio oral penal bajo el COIP de Ecuador.
El abogado está en la fase de ${phase === "contrainterrogatorio" ? "contrainterrogatorio" : "interrogatorio directo"}.
Clasifica lo que acaba de decir. Responde ÚNICAMENTE con una de estas tres palabras:
PREGUNTA — si es una pregunta, comentario o afirmación dirigida al testigo
FIN_DE_TURNO — si indica que terminó su turno (ej: "gracias", "hasta ahí", "no más preguntas", "he terminado", "gracias su señoría", "gracias señor juez", "puede retirarse", "eso es todo", "suficiente")
OBJECION — si dice "objeción" o expresa querer objetar algo
Una sola palabra. Sin explicaciones. Sin puntuación.`;
}

export function buildEvalPrompt(userRole) {
  return `Eres juez experto en litigación oral bajo el COIP de Ecuador. Evalúa el desempeño del ${userRole === "fiscal" ? "Fiscal" : "Defensor"} con rigor profesional basándote en la transcripción completa de todos los testigos.
Devuelve ÚNICAMENTE JSON válido sin backticks ni texto adicional:
{"puntaje_total":75,"categorias":{"tecnica_interrogatorio":{"puntaje":80,"comentario":"..."},"coherencia_teoria":{"puntaje":70,"comentario":"..."},"manejo_objeciones":{"puntaje":75,"comentario":"..."},"lenguaje_juridico":{"puntaje":65,"comentario":"..."}},"fortalezas":["...","...","..."],"areas_mejora":["...","...","..."],"mejor_pregunta":"La pregunta más efectiva del abogado con cita textual","peor_pregunta":"La pregunta más débil o irregular con cita textual y razón","recomendacion":"..."}`;
}
